// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { getErrorHelperText, isValueEmpty } from '../../../../utils/Utils';
import { useSingleAttributeQuery } from '../../../../features/configuration/attributeLibrary/api/attributeQueries';
import {
  composeDraftBlurHandler,
  scheduleDraftPersistOnBlur,
} from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

/**
 * Dropdown field component for selecting from a list of options
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @param {boolean} props.required - Whether the field is required
 * @returns {React.ReactElement} The Select component
 */
const DropdownFieldComponent = ({
  label,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  titleLabel,
  required = false,
  overrideDropdownOptions = null,
  isLoadingConstraints = false,
  configColumn = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { execute: fetchSingleAttributeDetails } = useSingleAttributeQuery({
    options: { enabled: false, loader: false },
  });
  const [fetchedOptions, setFetchedOptions] = useState(null);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);

  // Use fetched options if available, then override options, then original options
  const options = useMemo(() => {
    let optionsToUse = [];
    if (
      overrideDropdownOptions !== null &&
      overrideDropdownOptions !== undefined
    ) {
      optionsToUse = overrideDropdownOptions;
    } else if (!isValueEmpty(attribute?.drop_down_options)) {
      optionsToUse = attribute?.drop_down_options;
    } else if (fetchedOptions !== null) {
      optionsToUse = fetchedOptions;
    }

    if (typeof optionsToUse === 'string') {
      try {
        optionsToUse = JSON.parse(optionsToUse);
      } catch (_e) {
        optionsToUse = [];
      }
    }
    return Array.isArray(optionsToUse) ? optionsToUse : [];
  }, [fetchedOptions, overrideDropdownOptions, attribute?.drop_down_options]);

  const handleOpen = useCallback(async () => {
    // Only fetch if we have no options and haven't fetched yet and column config is true
    if (
      options?.length === 0 &&
      attribute?.id &&
      fetchedOptions === null &&
      configColumn
    ) {
      setIsFetchingOptions(true);
      try {
        const data = await fetchSingleAttributeDetails({
          params: { attribute_id: attribute.id },
        });
        const enrichedOptions = data?.drop_down_options || [];
        setFetchedOptions(enrichedOptions);
      } catch (_err) {
        setFetchedOptions([]);
      } finally {
        setIsFetchingOptions(false);
      }
    }
  }, [
    options.length,
    attribute?.id,
    fetchedOptions,
    configColumn,
    fetchSingleAttributeDetails,
  ]);

  const selectedOption = useMemo(() => {
    if (Array.isArray(options) && (value || value === 0)) {
      return (
        options?.find((option) => {
          const optionValue =
            typeof option === 'object' ? option?.value : option;
          return String(optionValue) === String(value);
        }) || null
      );
    }
    return null;
  }, [options, value]);

  /**
   * Handles dropdown change events
   * @function
   * @param {React.ChangeEvent<{}>} _e - The change event
   * @param {string|Object} newValue - The new value
   * @returns {void}
   */
  const handleChange = useCallback(
    (_e, newValue) => {
      const optionValue =
        newValue !== null && typeof newValue === 'object'
          ? newValue?.value
          : newValue;
      onChange(optionValue);
      if (newValue == null) {
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur]
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'object') {
      return option?.label || option?.value || '';
    }
    return String(option);
  }, []);

  const isOptionEqualToValue = useCallback((option, val) => {
    const optionValue = typeof option === 'object' ? option?.value : option;
    const valValue = typeof val === 'object' ? val?.value : val;
    return String(optionValue) === String(valValue);
  }, []);

  const { onBlur, name } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleInputBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(onBlur, onDraftBlur)(event);
    },
    [onBlur, onDraftBlur]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={typeof label === 'string' ? titleLabel : undefined}
        name={name}
        required={required}
        placeholder="Select value"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors[attribute?.id],
            entityDetailsFormikForm?.touched[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [
      label,
      titleLabel,
      name,
      required,
      attribute?.id,
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      hasDraftChip,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      disableClearable={required}
      onChange={handleChange}
      onBlur={handleInputBlur}
      onClose={handleClose}
      onOpen={handleOpen}
      loading={isLoadingConstraints || isFetchingOptions}
      loadingText="Loading options..."
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      readOnly={isLoadingConstraints}
      popupIcon={<DropdownArrow />}
      noOptionsText="No options present."
      renderInput={renderInput}
    />
  );
};

DropdownFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    drop_down_options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
      ])
    ),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  overrideDropdownOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  isLoadingConstraints: PropTypes.bool,
  configColumn: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default DropdownFieldComponent;
